import { useEffect, useState } from "react"
import { ModalPopUp } from "../../common/element/ModalPopUp"
import { Header } from "../../common/header/Header"
import { IModalMessage } from "../../../types/error/IModalMessage"
import { useNavigate } from "react-router-dom"
import { IReplenishmentListProjection } from "../../../types/replenishment/IReplenishmentListProjection"
import * as TrinityAPI from "../../../requests/TrinityAPI"
import { getMskTime, round2Digits, spacedNum } from "../../common/CommonHelper"
import { ReplenishmentStatus } from "../../../types/replenishment/ReplenishmentStatus"
import { IReplenishmentStatus } from "../../../types/replenishment/IReplenishmentStatus"
import { useStore } from "../../../stores/RootStoreContext"
import { MenuManager } from "../MenuManager"
import { MenuAdmin } from "../../admin/MenuAdmin"
import { IPageResponse } from "../../../types/IPageResponse"
import { ICheckConversationResponse } from "../../../types/messenger/ICheckConversationResponse"
import { Messenger } from "../../common/messenger/Messenger"
import { IMG } from "../../common/CONST"
import { CONFIG } from "../../../CONFIG"
import Button from "@/ui/components/Button"
import Input from "@/ui/components/Input"
import Skeleton from "@/ui/components/Skeleton"
import EmptyRow from "@/ui/components/EmptyRow"
import AXIOS from "@/requests/_axios"
import { ModalConfirm } from "@/components/common/element/ModalConfirm"

export const ManagerReplenishmentList = () => {
  const navigate = useNavigate()
  const { userStore } = useStore()

  const [modalMessage, setModalMessage] = useState<IModalMessage | null>(null)
  const [confirmMessage, setConfirmMessage] = useState<IModalMessage>()
  const [replenishments, setReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [search, setSearch] = useState("")
  const [filteredReplenishments, setFilteredReplenishments] = useState<IReplenishmentListProjection[]>([])
  const [status, setStatus] = useState("")
  const [sortByDoneDate, setSortByDoneDate] = useState("")

  /* MESSENGER FCN */
  const [instanceId, setInstanceId] = useState<string>()
  const [conversationUserId, setConversationUserId] = useState<number>()
  const CHAT_INSTANCE = "REPLENISHMENT"
  const [unreadList, setUnreadList] = useState<IPageResponse<ICheckConversationResponse>>()
  const [isLoading, setLoading] = useState(true)
  const [isRefetchLoading, setRefetchLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const fetchUnread = () => {
    TrinityAPI.getUnreadConversations(CHAT_INSTANCE, response => {
      if (response.status === 200) {
        setUnreadList(response.data)
      }
    })
  }

  const closeMessenger = () => {
    setInstanceId(undefined)
    setConversationUserId(undefined)
  }

  const showMessenger = (instanceId: string | undefined, userId: number | undefined) => {
    setInstanceId(instanceId)
    setConversationUserId(userId)
  }

  const getUnreadCounter = (instanceId: string | undefined): number => {
    let counter = 0
    if (!instanceId) return counter
    let unreadItem = unreadList?.items.filter(item => item.instanceId === instanceId)[0]
    counter = unreadItem ? unreadItem.num : 0
    return counter
  }

  /* EOF MESSENGER FCN */

  const fetchReplenishments = () => {
    setSearch("")

    TrinityAPI.getReplenishmentList(
      status,
      response => {
        if (response.status === 200) {
          setReplenishments(response.data)
          setFilteredReplenishments(response.data)
        } else {
          setModalMessage({
            title: "Ошибка!",
            text: `Ошибка получения пополнений: ${response.data.message}`
          })
        }
      },
      () => {
        setLoading(false)
        setRefetchLoading(false)
      },
      status === "DONE" ? sortByDoneDate || undefined : undefined
    )
  }

  const refetchData = () => {
    setRefetchLoading(true)
    fetchReplenishments()
  }

  const handleDownload = async () => {
    setIsDownloading(true)
    try {
      const response = await AXIOS.get("/trinity/api/v1/unload/xls/replenishments")

      if (response.status === 200) {
        const { data } = response

        const element = document.createElement("a")
        element.href = `data:application/vnd.ms-excel;base64,${response.data.fileBase64}`
        element.download = data.fileName
        document.body.appendChild(element)
        element.click()
        document.body.removeChild(element)
      }
    } catch (err) {
      setIsDownloading(false)
    }
    setIsDownloading(false)
  }

  const getRowClazz = (status: string) => {
    switch (status) {
      case "DOC":
      case "CR_REQ":
      case "BILL_PAID":
      case "CR_FIX":
        return "global-item-active"
      case "DONE":
        return "global-item-done"
      default:
        return "global-item"
    }
  }

  const getStatusClazz = (status: string) => {
    switch (status) {
      case "CR_NAC":
      case "DOC_NAC":
        return "status-no"
      case "DOC":
      case "CR_REQ":
      case "BILL_PAID":
        return "status-ok"
      default:
        return "status"
    }
  }

  const confirmRemove = (e: boolean | undefined, id: number) => {
    if (e) {
      remove(id)
    }
  }

  const remove = (id: number) => {
    TrinityAPI.deleteReplenishment(id, response => {
      if (response.status === 200) {
        fetchReplenishments()
      }
    })
  }

  useEffect(() => {
    if (status !== "DONE" && sortByDoneDate !== "") {
      setSortByDoneDate("")
      return // sortByDoneDate will be reset; effect will re-run with sortByDoneDate=""
    }
    fetchReplenishments()
  }, [status, sortByDoneDate])

  useEffect(() => {
    if (!search) {
      setFilteredReplenishments(replenishments)
      return
    }

    setFilteredReplenishments(
      replenishments.filter(
        r =>
          (r.contractor && r.contractor.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.country && r.country.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.amount && round2Digits(Number(r.amount) / 100).toString() === search.toLocaleLowerCase()) ||
          (r.clientName && r.clientName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.clientCompanyName && r.clientCompanyName.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1) ||
          (r.id && r.id.toString() === search) ||
          (r.currency && r.currency.toLocaleLowerCase() === search.toLocaleLowerCase())
      )
    )
  }, [search])

  useEffect(() => {
    fetchUnread()

    const intervalId = setInterval(() => {
      fetchUnread()
    }, CONFIG.fetchUnreadsInterval)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return (
    <>
      <ModalConfirm message={confirmMessage} setMessage={() => setConfirmMessage(undefined)} />
      <ModalPopUp message={modalMessage} close={() => setModalMessage(null)} />
      {userStore.isManager ? <MenuManager active={"replenishment"} /> : <MenuAdmin active={"replenishment"} />}
      <main className="main-container" id="scrollable">
        <Header />
        <div className="container">
          <h1 className="ui-heading">Пополнения рублевые</h1>

          <section className="header-chapter">
            <div className="headr-chapter-name">
              <div className="header-chapter-icon">
                <img src={IMG.deals} alt="" className="header-chapter-img" />
              </div>
              <Button variant="black" onClick={refetchData} loading={isRefetchLoading}>
                Обновить список пополнений
              </Button>
            </div>
            <Button onClick={handleDownload} variant="black" loading={isDownloading}>
              Сверка пополнений
            </Button>
          </section>

          <section className="search-block">
            <Input
              type="search"
              onChange={e => setSearch(e.target.value)}
              placeholder="Поиск по контрагенту, компании, имени клиента, стране, сумме или валюте"
            />
          </section>

          <div className="main-content--wrapper">
            <section className="main-content">
              <ul className="header-global-list">
                <li className="header-global-item width-130">Дата и номер</li>
                <li className="header-global-item width-130">Сумма</li>
                <li className="header-global-item width-100">Валюта</li>
                <li className="header-global-item cols-deals min-width-150">Получатель (Страна)</li>
                <li className="header-global-item width-70">Ставка</li>
                <li className="header-global-item cols-deals">Плательщик (Страна)</li>
                <li className="header-global-item cols-deals" style={{ paddingLeft: "10px" }}>
                  Клиент
                </li>
                <li className="header-global-item width-130">
                  <select value={status} className="select-style" onChange={e => setStatus(e.target.value)}>
                    <option value="">Статус</option>
                    <option value="ACTION_REQUIRED">Требуется действие</option>
                    {Object.keys(ReplenishmentStatus).map((key, index) => (
                      <option value={key} key={index}>
                        {ReplenishmentStatus[key as keyof IReplenishmentStatus][0]}
                      </option>
                    ))}
                  </select>
                  {status === "DONE" && (
                    <select
                      value={sortByDoneDate}
                      className="select-style"
                      onChange={e => setSortByDoneDate(e.target.value)}
                    >
                      <option value="">Сортировка</option>
                      <option value="asc">По дате (↑)</option>
                      <option value="desc">По дате (↓)</option>
                    </select>
                  )}
                </li>
                {userStore.isAdmin && <li className="header-global-item width-115 text-end">Действия</li>}
              </ul>
              <ul className="global-list">
                {isLoading && (
                  <div className="skeleton-wrapper">
                    <Skeleton rows={4} gutterTop />
                  </div>
                )}
                {!isLoading &&
                  (filteredReplenishments.length > 0 ? (
                    filteredReplenishments.map((r, index) => (
                      <li className={getRowClazz(r.status)} id="deals-info-open" key={index}>
                        <div
                          className="global-scroll-block"
                          onClick={() => window.open("/replenishment/" + r.id, "_blank")}
                        >
                          <p className="global-item-text width-130">
                            <span>№{r.id}</span>
                            <span
                              dangerouslySetInnerHTML={{
                                __html:
                                  getMskTime((r.status === "DONE" && r.doneDate ? r.doneDate : r.createdAt) || "") || ""
                              }}
                            />
                          </p>
                          <p className="global-item-text width-130">
                            {r.amount ? spacedNum(round2Digits(Number(r.amount) / 100)) : "-"}
                          </p>
                          <p className="global-item-text globak-num width-100">{r.currency || "-"}</p>
                          <p className="global-item-text cols-deals min-width-150">
                            <span className="deal-country">{r.country?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{r.contractor || "-"}</span>
                          </p>
                          <p className="global-item-text width-70">{r.rate ? `${r.rate} %` : "-"}</p>
                          <p className="global-item-text cols-deals">
                            <span className="deal-country">{r.companyCountry?.toUpperCase() || "-"}</span>
                            <span className="deal-name">{r.companyName || "-"}</span>
                          </p>
                          <p className="global-item-text cols-deals breakWord" style={{ paddingLeft: "10px" }}>
                            <span className="deal-country">{r.clientName || "-"}</span>
                            <span className="deal-name">{r.clientCompanyName || "-"}</span>
                          </p>
                          <p className={`${getStatusClazz(r.status)} width-130`}>
                            {r.status ? ReplenishmentStatus[r.status as keyof IReplenishmentStatus][0] : ""}
                          </p>
                        </div>
                        <button className="chat-deals" onClick={() => showMessenger(r.id?.toString(), r.userId)}>
                          {getUnreadCounter(r.id?.toString()) > 0 ? (
                            <span className="message-new">{getUnreadCounter(r.id?.toString())}</span>
                          ) : (
                            ""
                          )}
                        </button>
                        {userStore.isAdmin && (
                          <div className="header-global-item width-115 buttons-end">
                            <Button
                              variant="black"
                              onClick={() =>
                                setConfirmMessage({
                                  title: "Удалить пополнение?",
                                  text: "Вы действительно желаете удалить пополнение?",
                                  callbackValue: true,
                                  _callback: (val: any) => confirmRemove(val, r.id)
                                })
                              }
                            >
                              Удалить
                            </Button>
                          </div>
                        )}
                      </li>
                    ))
                  ) : (
                    <EmptyRow text="Пополнений не найдено" />
                  ))}
              </ul>
            </section>
          </div>
        </div>
        <Messenger instanceId={instanceId} type={CHAT_INSTANCE} userId={conversationUserId} close={closeMessenger} />
      </main>
    </>
  )
}
